package co.maytes.standupbot.dashboard.styles

import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.shared.design.StatusHue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.css.percent

private val phoneMedia = CSSMediaQuery.Raw("(max-width: ${Wizard.PHONE_MAX}px)")

private fun InitSilkContext.rule(selector: String, build: StyleScope.() -> Unit) {
    stylesheet.registerStyle(selector) { base { Modifier.styleModifier(build) } }
}

private fun InitSilkContext.mediaRule(selector: String, query: CSSMediaQuery, build: StyleScope.() -> Unit) {
    stylesheet.registerStyle(selector) { cssRule(query, null) { Modifier.styleModifier(build) } }
}

private fun InitSilkContext.hoverRule(selector: String, build: StyleScope.() -> Unit) =
    mediaRule(selector, HoverFinePointerMedia, build)

private fun accentTint(percent: String): String = Wizard.tint(Palette.accent, percent)

private val success = Palette.status(StatusHue.Success)
private val danger = Palette.status(StatusHue.Danger)

@InitSilk
fun initOnboardingStyles(ctx: InitSilkContext) {
    ctx.registerWizardKeyframes()
    ctx.registerWizardLayout()
    ctx.registerWizardStepper()
    ctx.registerWizardGraphics()
    ctx.registerWizardOptions()
    ctx.registerWizardFeedback()
    ctx.registerWizardLists()
    ctx.registerWizardPhone()
    ctx.registerWizardReducedMotion()
}

private fun InitSilkContext.registerWizardKeyframes() {
    stylesheet.registerKeyframes("sb-wiz-step-in") {
        from {
            Modifier.styleModifier {
                property("opacity", "0")
                property("transform", "translateX(${Wizard.SLIDE_DISTANCE})")
            }
        }
        to {
            Modifier.styleModifier {
                property("opacity", "1")
                property("transform", "translateX(0)")
            }
        }
    }
    stylesheet.registerKeyframes("sb-wiz-fill") {
        from { Modifier.styleModifier { property("transform", "scaleX(var(--wiz-from))") } }
        to { Modifier.styleModifier { property("transform", "scaleX(var(--wiz-to))") } }
    }
    stylesheet.registerKeyframes("sb-wiz-pop") {
        0.percent {
            Modifier.styleModifier {
                property("transform", "scale(${Wizard.POP_FROM})")
                property("opacity", "0")
            }
        }
        60.percent {
            Modifier.styleModifier {
                property("transform", "scale(${Wizard.POP_PEAK})")
                property("opacity", "1")
            }
        }
        100.percent {
            Modifier.styleModifier {
                property("transform", "scale(1)")
                property("opacity", "1")
            }
        }
    }
    stylesheet.registerKeyframes("sb-wiz-flow") {
        0.percent {
            Modifier.styleModifier {
                property("left", "0")
                property("opacity", "0")
            }
        }
        20.percent { Modifier.styleModifier { property("opacity", "1") } }
        80.percent { Modifier.styleModifier { property("opacity", "1") } }
        100.percent {
            Modifier.styleModifier {
                property("left", "100%")
                property("opacity", "0")
            }
        }
    }
}

private fun InitSilkContext.registerWizardLayout() {
    rule(".sb-wiz") {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", Spacing.lg.toString())
        property("width", "100%")
        property("min-width", "0")
    }
    rule(".sb-wiz-panel") {
        property("display", "flex")
        property("flex-direction", "column")
        property("background-color", Palette.panel.cssRgb())
        property("border", "1px solid ${Palette.border.cssRgb()}")
        property("border-radius", Radius.xl.toString())
        property("box-shadow", Shadow.SM)
        property("overflow", "hidden")
        property("animation", "sb-wiz-step-in ${Wizard.DUR_STEP_IN} ${Motion.EASE_EMPHASIS} both")
    }
    rule(".sb-wiz-art") {
        property("display", "flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("min-height", Wizard.ART_HEIGHT)
        property("padding", Spacing.lg.toString())
        property("border-bottom", "1px solid ${Palette.borderSubtle.cssRgb()}")
        property(
            "background-image",
            "radial-gradient(ellipse 70% 120% at 50% 100%, ${accentTint(Wizard.WASH_PERCENT)}, transparent 75%)",
        )
    }
    rule(".sb-wiz-body") {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", Spacing.lg.toString())
        property("padding", Spacing.lg.toString())
    }
    rule(".sb-wiz-head") {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", Spacing.xxs.toString())
    }
    rule(".sb-wiz-title") {
        property("margin", "0")
        property("font-size", Typography.h2.size.toString())
        property("font-weight", FontWeight.semibold.toString())
        property("line-height", "${Typography.h2.lineHeight}")
        property("letter-spacing", Tracking.tight.toString())
        property("color", Palette.textPrimary.cssRgb())
        property("max-width", Wizard.TITLE_MEASURE)
    }
    rule(".sb-wiz-sub") {
        property("margin", "0")
        property("font-size", Typography.body.size.toString())
        property("line-height", "${Typography.body.lineHeight}")
        property("color", Palette.textSecondary.cssRgb())
        property("max-width", Wizard.SUB_MEASURE)
    }
    rule(".sb-wiz-footer") {
        property("display", "flex")
        property("align-items", "center")
        property("justify-content", "space-between")
        property("gap", Spacing.sm.toString())
        property("padding", "${Spacing.md} ${Spacing.lg}")
        property("border-top", "1px solid ${Palette.borderSubtle.cssRgb()}")
        property("background-color", Palette.surfaceSubtle.cssRgb())
    }
    rule(".sb-wiz-footer-start") {
        property("display", "flex")
        property("align-items", "center")
        property("gap", Spacing.sm.toString())
    }
    rule(".sb-wiz-footer-end") {
        property("display", "flex")
        property("align-items", "center")
        property("justify-content", "flex-end")
        property("gap", Spacing.sm.toString())
        property("margin-left", "auto")
    }
    rule(".sb-wiz-section") {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", Spacing.md.toString())
    }
    rule(".sb-wiz-section + .sb-wiz-section") {
        property("padding-top", Spacing.lg.toString())
        property("border-top", "1px solid ${Palette.borderSubtle.cssRgb()}")
    }
    rule(".sb-wiz-section-head") {
        property("display", "flex")
        property("align-items", "center")
        property("gap", Spacing.sm.toString())
    }
    rule(".sb-wiz-section-text") {
        property("display", "flex")
        property("flex-direction", "column")
        property("min-width", "0")
    }
    rule(".sb-wiz-section-action") {
        property("margin-left", "auto")
        property("flex-shrink", "0")
    }
    rule(".sb-wiz-section-title") {
        property("font-size", Typography.label.size.toString())
        property("font-weight", FontWeight.semibold.toString())
        property("color", Palette.textPrimary.cssRgb())
    }
    rule(".sb-wiz-section-hint") {
        property("font-size", Typography.bodySmall.size.toString())
        property("color", Palette.textSecondary.cssRgb())
    }
    rule(".sb-wiz-icon-chip") {
        property("display", "inline-grid")
        property("place-items", "center")
        property("flex-shrink", "0")
        property("width", Wizard.ICON_CHIP)
        property("height", Wizard.ICON_CHIP)
        property("border-radius", Radius.lg.toString())
        property("background-color", Palette.accentSubtle.cssRgb())
        property("color", Palette.accentText.cssRgb())
    }
    rule(".sb-wiz-label") {
        property("font-size", Typography.caption.size.toString())
        property("font-weight", FontWeight.medium.toString())
        property("letter-spacing", Tracking.caps.toString())
        property("text-transform", "none")
        property("color", Palette.textTertiary.cssRgb())
    }
    rule(".sb-wiz-stack") {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", Spacing.sm.toString())
    }
    rule(".sb-wiz-actions") {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("align-items", "center")
        property("gap", Spacing.sm.toString())
    }
}

private fun InitSilkContext.registerWizardStepper() {
    rule(".sb-wiz-stepper") {
        property("position", "relative")
    }
    rule(".sb-wiz-steps") {
        property("position", "relative")
        property("display", "grid")
        property("grid-template-columns", "repeat(var(--wiz-n), minmax(0, 1fr))")
        property("margin", "0")
        property("padding", "0")
        property("list-style", "none")
    }
    rule(".sb-wiz-track") {
        property("position", "absolute")
        property("top", "calc((${Wizard.NODE} - ${Wizard.LINE}) / 2)")
        property("left", "calc(100% / (var(--wiz-n) * 2))")
        property("right", "calc(100% / (var(--wiz-n) * 2))")
        property("height", Wizard.LINE)
        property("border-radius", Radius.pill.toString())
        property("background-color", Palette.border.cssRgb())
        property("overflow", "hidden")
    }
    rule(".sb-wiz-track-fill") {
        property("position", "absolute")
        property("inset", "0")
        property("background-color", Palette.accent.cssRgb())
        property("transform-origin", "left center")
        property("transform", "scaleX(var(--wiz-to))")
        property("animation", "sb-wiz-fill ${Wizard.DUR_FILL} ${Motion.EASE_EMPHASIS} both")
    }
    rule(".sb-wiz-step") {
        property("position", "relative")
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "center")
        property("gap", Spacing.xs.toString())
        property("min-width", "0")
    }
    rule(".sb-wiz-node") {
        property("display", "grid")
        property("place-items", "center")
        property("width", Wizard.NODE)
        property("height", Wizard.NODE)
        property("box-sizing", "border-box")
        property("border-radius", Radius.pill.toString())
        property("border", "${Wizard.LINE} solid ${Palette.border.cssRgb()}")
        property("background-color", Palette.canvas.cssRgb())
        property("color", Palette.textTertiary.cssRgb())
        property("font-size", Typography.bodySmall.size.toString())
        property("font-weight", FontWeight.semibold.toString())
        property("font-variant-numeric", "tabular-nums")
        property("transition", Motion.TRANSITION_BASE)
    }
    rule(".sb-wiz-node--current") {
        property("border-color", Palette.accent.cssRgb())
        property("color", Palette.accentText.cssRgb())
        property("background-color", Palette.panel.cssRgb())
        property("box-shadow", "0 0 0 ${Wizard.RING_WIDTH} ${accentTint(Wizard.RING_PERCENT)}")
        property("animation", "sb-wiz-pop ${Wizard.DUR_POP} ${Motion.EASE_EMPHASIS} both")
    }
    rule(".sb-wiz-node--done") {
        property("border-color", Palette.accent.cssRgb())
        property("background-color", Palette.accent.cssRgb())
        property("color", Palette.onAccent.cssRgb())
    }
    rule(".sb-wiz-node--fresh") {
        property("animation", "sb-wiz-pop ${Wizard.DUR_POP} ${Motion.EASE_EMPHASIS} both")
    }
    rule(".sb-wiz-step-label") {
        property("font-size", Typography.caption.size.toString())
        property("font-weight", FontWeight.medium.toString())
        property("color", Palette.textTertiary.cssRgb())
        property("white-space", "nowrap")
        property("transition", Motion.TRANSITION_BASE)
    }
    rule(".sb-wiz-step-label--current") {
        property("color", Palette.textPrimary.cssRgb())
        property("font-weight", FontWeight.semibold.toString())
    }
    rule(".sb-wiz-step-label--done") {
        property("color", Palette.textSecondary.cssRgb())
    }
}

private fun InitSilkContext.registerWizardGraphics() {
    rule(".sb-brand-chip") {
        property("position", "relative")
        property("display", "inline-grid")
        property("place-items", "center")
        property("flex-shrink", "0")
        property("width", "var(--chip, ${Wizard.CHIP_MD})")
        property("height", "var(--chip, ${Wizard.CHIP_MD})")
        property("box-sizing", "border-box")
        property("border-radius", Radius.lg.toString())
        property("background-color", BrandChipColors.BACKGROUND)
        property("border", "1px solid ${BrandChipColors.BORDER}")
        property("box-shadow", Shadow.XS)
        property("color", BrandChipColors.INK)
    }
    rule(".sb-brand-chip img") {
        property("width", "52%")
        property("height", "52%")
        property("object-fit", "contain")
        property("display", "block")
    }
    rule(".sb-brand-chip--sm") { property("--chip", Wizard.CHIP_SM) }
    rule(".sb-brand-chip--lg") { property("--chip", Wizard.CHIP_LG) }
    rule(".sb-brand-chip--bleed") { property("overflow", "hidden") }
    rule(".sb-brand-chip--bleed img") {
        property("width", "100%")
        property("height", "100%")
        property("object-fit", "cover")
    }
    rule(".sb-brand-badge") {
        property("position", "absolute")
        property("right", "calc(${Wizard.BADGE} / -3)")
        property("bottom", "calc(${Wizard.BADGE} / -3)")
        property("display", "grid")
        property("place-items", "center")
        property("width", Wizard.BADGE)
        property("height", Wizard.BADGE)
        property("border-radius", Radius.pill.toString())
        property("background-color", success.solid.cssRgb())
        property("color", success.onSolid.cssRgb())
        property("border", "${Wizard.LINE} solid ${BrandChipColors.BACKGROUND}")
        property("animation", "sb-wiz-pop ${Wizard.DUR_POP} ${Motion.EASE_EMPHASIS} both")
    }
    rule(".sb-wiz-flow") {
        property("display", "flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("gap", Spacing.sm.toString())
    }
    rule(".sb-wiz-link") {
        property("position", "relative")
        property("flex", "0 1 ${Wizard.LINK_WIDTH}")
        property("width", Wizard.LINK_WIDTH)
        property("height", Wizard.LINE)
        property("background-image", "linear-gradient(to right, ${Palette.textTertiary.cssRgb()} 50%, transparent 50%)")
        property("background-size", "${Spacing.xs} ${Wizard.LINE}")
        property("opacity", "0.6")
        property("transition", Motion.TRANSITION_BASE)
    }
    rule(".sb-wiz-link--live") {
        property("background-image", "none")
        property("background-color", Palette.accent.cssRgb())
        property("opacity", "1")
    }
    rule(".sb-wiz-link--flowing::after") {
        property("content", "''")
        property("position", "absolute")
        property("top", "50%")
        property("width", Wizard.DOT)
        property("height", Wizard.DOT)
        property("margin-top", "calc(${Wizard.DOT} / -2)")
        property("margin-left", "calc(${Wizard.DOT} / -2)")
        property("border-radius", Radius.pill.toString())
        property("background-color", Palette.accent.cssRgb())
        property("animation", "sb-wiz-flow ${Wizard.DUR_FLOW} ${Motion.EASE_STANDARD} infinite")
    }
    rule(".sb-wiz-conn") {
        property("display", "flex")
        property("align-items", "center")
        property("gap", Spacing.md.toString())
        property("padding", Spacing.md.toString())
        property("border", "1px solid ${Palette.border.cssRgb()}")
        property("border-radius", Radius.lg.toString())
        property("background-color", Palette.surface.cssRgb())
        property("transition", Motion.TRANSITION_BASE)
    }
    rule(".sb-wiz-conn--live") {
        property("border-color", success.border.cssRgb())
        property("background-color", Wizard.tint(success.solid, Wizard.TINT_PERCENT))
    }
    rule(".sb-wiz-conn-text") {
        property("display", "flex")
        property("flex-direction", "column")
        property("flex", "1 1 auto")
        property("min-width", "0")
    }
    rule(".sb-wiz-conn-title") {
        property("font-size", Typography.body.size.toString())
        property("font-weight", FontWeight.semibold.toString())
        property("color", Palette.textPrimary.cssRgb())
    }
    rule(".sb-wiz-conn-detail") {
        property("font-size", Typography.bodySmall.size.toString())
        property("color", Palette.textSecondary.cssRgb())
    }
    rule(".sb-wiz-bar") {
        property("width", "5.5rem")
        property("height", Typography.label.size.toString())
        property("border-radius", Radius.pill.toString())
        property("background-color", Palette.surfaceSubtle.cssRgb())
        property("animation", "sb-fade-pulse ${Wizard.DUR_PULSE} ease-in-out infinite")
    }
    rule(".sb-wiz-seal") {
        property("display", "inline-grid")
        property("place-items", "center")
        property("flex-shrink", "0")
        property("width", Wizard.SEAL)
        property("height", Wizard.SEAL)
        property("border-radius", Radius.pill.toString())
        property("background-color", success.solid.cssRgb())
        property("color", success.onSolid.cssRgb())
        property("animation", "sb-wiz-pop ${Wizard.DUR_POP} ${Motion.EASE_EMPHASIS} both")
    }
    rule(".sb-wiz-seal--danger") {
        property("background-color", danger.solid.cssRgb())
        property("color", danger.onSolid.cssRgb())
    }
    rule(".sb-wiz-seal--large") {
        property("width", Wizard.SEAL_LARGE)
        property("height", Wizard.SEAL_LARGE)
        property("box-shadow", "0 0 0 ${Spacing.sm} ${Wizard.tint(success.solid, Wizard.RING_PERCENT)}")
    }
}

private fun InitSilkContext.registerWizardOptions() {
    rule(".sb-wiz-opt") {
        property("display", "flex")
        property("align-items", "center")
        property("gap", Spacing.md.toString())
        property("width", "100%")
        property("box-sizing", "border-box")
        property("padding", "${Spacing.sm} ${Spacing.md}")
        property("border", "1px solid ${Palette.border.cssRgb()}")
        property("border-radius", Radius.lg.toString())
        property("background-color", Palette.surface.cssRgb())
        property("color", Palette.textPrimary.cssRgb())
        property("font-family", "inherit")
        property("text-align", "left")
        property("cursor", "pointer")
        property(
            "transition",
            Motion.transition(Wizard.DUR_LIFT, "transform", "box-shadow", "border-color", "background-color"),
        )
    }
    hoverRule(".sb-wiz-opt:hover") {
        property("transform", "translateY(${Wizard.LIFT})")
        property("box-shadow", Shadow.SM)
        property("border-color", Palette.textTertiary.cssRgb())
    }
    rule(".sb-wiz-opt:active") {
        property("transform", "scale(${Wizard.PRESS_SCALE})")
    }
    rule(".sb-wiz-opt:focus-visible") {
        property("outline", FocusRing.OUTLINE)
        property("outline-offset", FocusRing.OFFSET)
    }
    rule(".sb-wiz-opt--selected, .sb-wiz-opt--selected:hover") {
        property("border-color", Palette.accent.cssRgb())
        property("box-shadow", "0 0 0 ${Wizard.HAIRLINE} ${Palette.accent.cssRgb()}")
        property("background-color", Wizard.tint(Palette.accent, Wizard.TINT_PERCENT))
    }
    rule(".sb-wiz-opt-lead") {
        property("display", "inline-grid")
        property("place-items", "center")
        property("flex-shrink", "0")
        property("width", Wizard.ICON_CHIP)
        property("height", Wizard.ICON_CHIP)
        property("border-radius", Radius.control.toString())
        property("background-color", Palette.surfaceSubtle.cssRgb())
        property("color", Palette.textSecondary.cssRgb())
    }
    rule(".sb-wiz-opt--selected .sb-wiz-opt-lead") {
        property("background-color", Palette.accentSubtle.cssRgb())
        property("color", Palette.accentText.cssRgb())
    }
    rule(".sb-wiz-opt-text") {
        property("display", "flex")
        property("flex-direction", "column")
        property("flex", "1 1 auto")
        property("min-width", "0")
    }
    rule(".sb-wiz-opt-title") {
        property("font-size", Typography.body.size.toString())
        property("font-weight", FontWeight.medium.toString())
    }
    rule(".sb-wiz-opt-desc") {
        property("font-size", Typography.bodySmall.size.toString())
        property("color", Palette.textSecondary.cssRgb())
    }
    rule(".sb-wiz-opt-mark") {
        property("display", "grid")
        property("place-items", "center")
        property("flex-shrink", "0")
        property("width", Wizard.MARK)
        property("height", Wizard.MARK)
        property("box-sizing", "border-box")
        property("border", "${Wizard.LINE} solid ${Palette.border.cssRgb()}")
        property("border-radius", Radius.pill.toString())
        property("color", Palette.onAccent.cssRgb())
        property("transition", Motion.TRANSITION_BASE)
    }
    rule(".sb-wiz-opt--selected .sb-wiz-opt-mark") {
        property("border-color", Palette.accent.cssRgb())
        property("background-color", Palette.accent.cssRgb())
    }
}

private fun InitSilkContext.registerWizardFeedback() {
    rule(".sb-wiz-chip") {
        property("display", "inline-flex")
        property("align-items", "center")
        property("flex-shrink", "0")
        property("gap", Spacing.xxs.toString())
        property("padding", "${Spacing.xxs} ${Spacing.sm}")
        property("border-radius", Radius.pill.toString())
        property("border", "1px solid transparent")
        property("font-size", Typography.caption.size.toString())
        property("font-weight", FontWeight.medium.toString())
        property("white-space", "nowrap")
    }
    rule(".sb-wiz-chip--neutral") {
        property("background-color", Palette.surfaceSubtle.cssRgb())
        property("border-color", Palette.borderSubtle.cssRgb())
        property("color", Palette.textSecondary.cssRgb())
    }
    rule(".sb-wiz-chip--success") {
        property("background-color", success.subtle.cssRgb())
        property("border-color", success.border.cssRgb())
        property("color", success.onSubtle.cssRgb())
    }
    rule(".sb-wiz-chip--danger") {
        property("background-color", danger.subtle.cssRgb())
        property("border-color", danger.border.cssRgb())
        property("color", danger.onSubtle.cssRgb())
    }
    rule(".sb-wiz-notice") {
        property("display", "flex")
        property("align-items", "center")
        property("gap", Spacing.md.toString())
        property("padding", Spacing.md.toString())
        property("border", "1px solid ${Palette.border.cssRgb()}")
        property("border-radius", Radius.lg.toString())
        property("background-color", Palette.surfaceSubtle.cssRgb())
    }
    rule(".sb-wiz-notice--success") {
        property("border-color", success.border.cssRgb())
        property("background-color", success.subtle.cssRgb())
    }
    rule(".sb-wiz-notice--danger") {
        property("border-color", danger.border.cssRgb())
        property("background-color", danger.subtle.cssRgb())
    }
    rule(".sb-wiz-notice-text") {
        property("display", "flex")
        property("flex-direction", "column")
        property("flex", "1 1 auto")
        property("min-width", "0")
    }
    rule(".sb-wiz-notice-title") {
        property("font-size", Typography.body.size.toString())
        property("font-weight", FontWeight.semibold.toString())
        property("color", Palette.textPrimary.cssRgb())
    }
    rule(".sb-wiz-notice-message") {
        property("font-size", Typography.bodySmall.size.toString())
        property("color", Palette.textSecondary.cssRgb())
    }
    rule(".sb-wiz-notice-action") {
        property("flex-shrink", "0")
    }
    rule(".sb-wiz-hero") {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "center")
        property("gap", Spacing.sm.toString())
        property("text-align", "center")
    }
}

private fun InitSilkContext.registerWizardLists() {
    rule(".sb-wiz-list") {
        property("display", "flex")
        property("flex-direction", "column")
        property("margin", "0")
        property("padding", "0")
        property("list-style", "none")
        property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
        property("border-radius", Radius.lg.toString())
        property("overflow", "hidden")
        property("background-color", Palette.surface.cssRgb())
    }
    rule(".sb-wiz-list > * + *") {
        property("border-top", "1px solid ${Palette.borderSubtle.cssRgb()}")
    }
    rule(".sb-wiz-row") {
        property("display", "flex")
        property("align-items", "center")
        property("flex-wrap", "wrap")
        property("gap", Spacing.sm.toString())
        property("padding", "${Spacing.sm} ${Spacing.md}")
        property("font-size", Typography.body.size.toString())
        property("color", Palette.textPrimary.cssRgb())
    }
    rule(".sb-wiz-row-text") {
        property("display", "flex")
        property("flex-direction", "column")
        property("flex", "1 1 auto")
        property("min-width", "0")
    }
    rule(".sb-wiz-row-note") {
        property("font-size", Typography.bodySmall.size.toString())
        property("color", Palette.textSecondary.cssRgb())
    }
    rule(".sb-wiz-row-value") {
        property("margin-left", "auto")
        property("font-weight", FontWeight.medium.toString())
        property("color", Palette.textPrimary.cssRgb())
        property("text-align", "right")
    }
    rule(".sb-wiz-tick") {
        property("display", "inline-grid")
        property("place-items", "center")
        property("flex-shrink", "0")
        property("width", Wizard.TICK)
        property("height", Wizard.TICK)
        property("box-sizing", "border-box")
        property("border-radius", Radius.pill.toString())
        property("border", "${Wizard.LINE} solid ${Palette.border.cssRgb()}")
        property("color", Palette.onAccent.cssRgb())
    }
    rule(".sb-wiz-tick--done") {
        property("border-color", success.solid.cssRgb())
        property("background-color", success.solid.cssRgb())
        property("color", success.onSolid.cssRgb())
    }
    rule(".sb-wiz-timeline") {
        property("display", "flex")
        property("flex-direction", "column")
        property("margin", "0")
        property("padding", "0")
        property("list-style", "none")
    }
    rule(".sb-wiz-tl-item") {
        property("position", "relative")
        property("display", "flex")
        property("align-items", "center")
        property("gap", Spacing.md.toString())
        property("padding-bottom", Spacing.md.toString())
    }
    rule(".sb-wiz-tl-item:last-child") {
        property("padding-bottom", "0")
    }
    rule(".sb-wiz-tl-item:not(:last-child)::before") {
        property("content", "''")
        property("position", "absolute")
        property("left", "calc(${Wizard.CHIP_MD} / 2 - ${Wizard.LINE} / 2)")
        property("top", Wizard.CHIP_MD)
        property("bottom", "0")
        property("width", Wizard.LINE)
        property("border-radius", Radius.pill.toString())
        property("background-color", Palette.borderSubtle.cssRgb())
    }
    rule(".sb-wiz-tl-text") {
        property("display", "flex")
        property("flex-direction", "column")
        property("min-width", "0")
    }
}

private fun InitSilkContext.registerWizardPhone() {
    mediaRule(".sb-wiz-body", phoneMedia) { property("padding", Spacing.md.toString()) }
    mediaRule(".sb-wiz-footer", phoneMedia) {
        property("padding", "${Spacing.sm} ${Spacing.md}")
        property("flex-wrap", "wrap")
    }
    mediaRule(".sb-wiz-art", phoneMedia) { property("padding", Spacing.md.toString()) }
    mediaRule(".sb-wiz-title", phoneMedia) { property("font-size", Typography.h3.size.toString()) }
    mediaRule(".sb-wiz-link", phoneMedia) {
        property("width", Wizard.LINK_WIDTH_PHONE)
        property("flex-basis", Wizard.LINK_WIDTH_PHONE)
    }
    mediaRule(".sb-wiz-flow", phoneMedia) { property("gap", Spacing.xs.toString()) }
    mediaRule(".sb-wiz-footer-start", phoneMedia) { property("gap", Spacing.xxs.toString()) }
    mediaRule(".sb-wiz-notice", phoneMedia) { property("flex-wrap", "wrap") }
}

private fun InitSilkContext.registerWizardReducedMotion() {
    reducedMotionRule(".sb-wiz-panel") { property("animation-name", "sb-enter-fade !important") }
    reducedMotionRule(".sb-wiz-track-fill") { property("animation", "none !important") }
    reducedMotionRule(".sb-wiz-node--current, .sb-wiz-node--fresh") { property("animation", "none !important") }
    reducedMotionRule(".sb-brand-badge, .sb-wiz-seal") { property("animation", "none !important") }
    reducedMotionRule(".sb-wiz-link--flowing::after") { property("animation", "none !important") }
    reducedMotionRule(".sb-wiz-opt, .sb-wiz-opt:hover, .sb-wiz-opt:active") { property("transform", "none !important") }
    reducedMotionRule(".sb-wiz-bar") { property("animation", "none !important") }
}
